import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { format } from "date-fns";
import { CalendarBlank, SignOut, CaretDown } from "@phosphor-icons/react";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import KPIHeader from "../components/KPIHeader";
import SessionsTable from "../components/SessionsTable";
import SessionFilters from "../components/SessionFilters";
import SessionDetailModal from "../components/SessionDetailModal";
import IntegrationTab from "../components/IntegrationTab";
import ShopifyIntegrationTab from "../components/ShopifyIntegrationTab";
import IntegrationTester from "../components/IntegrationTester";
import CouponManagement from "../components/CouponManagement";
import LivePricesTab from "../components/LivePricesTab";
import AdminSettingsTab from "../components/AdminSettingsTab";
import ScoringRulesTab from "../components/ScoringRulesTab";
import UserManagement from "../components/UserManagement";
import RolePermissionsTab from "../components/RolePermissionsTab";
import ScraperUrlsTab from "../components/ScraperUrlsTab";
import KnowledgeBaseTab from "../components/KnowledgeBaseTab";
import ChatWidget from "../components/ChatWidget";
import { useAuth } from "../context/AuthContext";
import { Toaster } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Configure axios to send cookies with every request
axios.defaults.withCredentials = true;

const LiveIndicator = ({ connected }) => (
  <div className="flex items-center gap-2 text-xs" data-testid="live-indicator">
    <span
      className={`w-2 h-2 rounded-full ${connected ? "bg-[#32D74B] animate-pulse" : "bg-[#FF3B30]"}`}
    />
    <span className={connected ? "text-[#32D74B]" : "text-[#FF3B30]"}>
      {connected ? "Live" : "Reconnecting..."}
    </span>
  </div>
);

function DateRangePicker({ dateRange, onDateRangeChange }) {
  const [open, setOpen] = useState(false);

  const handleSelect = (range) => {
    onDateRangeChange(range);
    if (range?.from && range?.to) {
      setOpen(false);
    }
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onDateRangeChange(undefined);
    setOpen(false);
  };

  const label = dateRange?.from
    ? dateRange.to
      ? `${format(dateRange.from, "MMM d, yyyy")} - ${format(dateRange.to, "MMM d, yyyy")}`
      : format(dateRange.from, "MMM d, yyyy")
    : "All time";

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          data-testid="date-range-picker-trigger"
          className="flex items-center gap-2 px-3 py-1.5 text-xs bg-[#121212] border border-[#2A2A2A] rounded hover:border-[#444] transition-colors text-[#A0A0A0]"
        >
          <CalendarBlank size={14} weight="bold" className="text-[#E85D24]" />
          <span>{label}</span>
          <CaretDown size={12} />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-auto p-0 bg-[#121212] border-[#2A2A2A]"
        align="end"
      >
        <div className="p-2 border-b border-[#2A2A2A] flex items-center justify-between">
          <span className="text-xs text-[#888]">Select date range</span>
          {dateRange?.from && (
            <button
              onClick={handleClear}
              data-testid="date-range-clear"
              className="text-[10px] text-[#E85D24] hover:text-[#E85D24]/80 transition-colors"
            >
              Clear
            </button>
          )}
        </div>
        <Calendar
          mode="range"
          selected={dateRange}
          onSelect={handleSelect}
          numberOfMonths={2}
          disabled={{ after: new Date() }}
          classNames={{
            months: "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0",
            month: "space-y-4",
            caption: "flex justify-center pt-1 relative items-center",
            caption_label: "text-sm font-medium text-white",
            nav: "space-x-1 flex items-center",
            nav_button: "h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100 inline-flex items-center justify-center rounded-md border border-[#2A2A2A] text-white",
            nav_button_previous: "absolute left-1",
            nav_button_next: "absolute right-1",
            table: "w-full border-collapse space-y-1",
            head_row: "flex",
            head_cell: "text-[#707070] rounded-md w-8 font-normal text-[0.8rem]",
            row: "flex w-full mt-2",
            cell: "relative p-0 text-center text-sm focus-within:relative focus-within:z-20 [&:has([aria-selected])]:bg-[#E85D24]/20 [&:has([aria-selected].day-outside)]:bg-[#E85D24]/10 [&:has([aria-selected].day-range-end)]:rounded-r-md [&:has(>.day-range-end)]:rounded-r-md [&:has(>.day-range-start)]:rounded-l-md first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md",
            day: "h-8 w-8 p-0 font-normal aria-selected:opacity-100 text-white hover:bg-[#2A2A2A] rounded-md inline-flex items-center justify-center",
            day_range_start: "day-range-start",
            day_range_end: "day-range-end",
            day_selected: "bg-[#E85D24] text-white hover:bg-[#E85D24] hover:text-white focus:bg-[#E85D24] focus:text-white",
            day_today: "bg-[#2A2A2A] text-white",
            day_outside: "day-outside text-[#555] aria-selected:bg-[#E85D24]/10 aria-selected:text-[#888]",
            day_disabled: "text-[#555] opacity-50",
            day_range_middle: "aria-selected:bg-[#E85D24]/20 aria-selected:text-white",
            day_hidden: "invisible",
          }}
        />
      </PopoverContent>
    </Popover>
  );
}

export default function Dashboard() {
  const { user, logout, hasPermission } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [kpis, setKpis] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("sessions");
  const [wsConnected, setWsConnected] = useState(false);
  const [selectedSessionId, setSelectedSessionId] = useState(null);
  const [dateRange, setDateRange] = useState(undefined);
  const [filters, setFilters] = useState({
    search: "",
    status: "all",
    channel: "all",
    handoff: "all",
    sort: "newest"
  });
  const wsRef = useRef(null);
  const reconnectTimerRef = useRef(null);
  const fetchDataRef = useRef(null);

  // Build query string from filters + date range
  const buildQuery = useCallback((f, dr) => {
    const params = new URLSearchParams();
    if (f.search) params.set("search", f.search);
    if (f.status !== "all") params.set("status", f.status);
    if (f.channel !== "all") params.set("channel", f.channel);
    if (f.handoff !== "all") params.set("handoff", f.handoff);
    if (f.sort !== "newest") params.set("sort", f.sort);
    if (dr?.from) params.set("from", format(dr.from, "yyyy-MM-dd"));
    if (dr?.to) params.set("to", format(dr.to, "yyyy-MM-dd"));
    const qs = params.toString();
    return qs ? `?${qs}` : "";
  }, []);

  const fetchData = useCallback(async (currentFilters, currentDateRange) => {
    try {
      const f = currentFilters || filters;
      const dr = currentDateRange !== undefined ? currentDateRange : dateRange;
      const query = buildQuery(f, dr);

      // Build KPI params separately (only date range)
      const kpiParams = new URLSearchParams();
      if (dr?.from) kpiParams.set("from", format(dr.from, "yyyy-MM-dd"));
      if (dr?.to) kpiParams.set("to", format(dr.to, "yyyy-MM-dd"));
      const kpiQs = kpiParams.toString() ? `?${kpiParams.toString()}` : "";

      // Auth flows via the httpOnly access_token cookie (withCredentials is set globally).
      const [sessionsRes, kpisRes] = await Promise.all([
        axios.get(`${API}/sessions${query}`),
        axios.get(`${API}/kpis${kpiQs}`)
      ]);
      setSessions(sessionsRes.data);
      setKpis(kpisRes.data);
    } catch (err) {
      console.error("Failed to fetch data:", err);
      if (err.response?.status === 401) {
        logout();
      }
    } finally {
      setLoading(false);
    }
  }, [buildQuery, filters, dateRange, logout]);

  // Keep latest fetchData in a ref so WebSocket handler always calls the freshest version
  // without forcing the WS effect to re-run (and reconnect) on every filter/dateRange change.
  useEffect(() => {
    fetchDataRef.current = fetchData;
  }, [fetchData]);

  // WebSocket connection
  useEffect(() => {
    function connect() {
      // Build the WS URL robustly:
      //  - If REACT_APP_BACKEND_URL is an absolute URL (dev preview), derive
      //    protocol+host from it.
      //  - Otherwise (empty / relative — the single-service Railway deploy),
      //    use the current page's origin. This is what fixes the
      //    `ws://api/ws` malformed URL bug on the production deploy.
      let wsUrl;
      if (BACKEND_URL && /^https?:\/\//.test(BACKEND_URL)) {
        const wsProtocol = BACKEND_URL.startsWith("https") ? "wss" : "ws";
        const wsHost = BACKEND_URL.replace(/^https?:\/\//, "");
        wsUrl = `${wsProtocol}://${wsHost}/api/ws`;
      } else {
        const wsProtocol = window.location.protocol === "https:" ? "wss" : "ws";
        wsUrl = `${wsProtocol}://${window.location.host}/api/ws`;
      }

      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        console.log("WebSocket connected");
        setWsConnected(true);
      };

      ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data);
          if (["session_created", "session_update", "score_update", "hot_alert", "handoff", "demo_booked"].includes(msg.type)) {
            if (fetchDataRef.current) fetchDataRef.current();
          }
        } catch (parseErr) {
          // Non-JSON WS frame (e.g., keepalive ping) — safe to ignore.
          console.debug("Dashboard WS: non-JSON message", parseErr);
        }
      };

      ws.onclose = () => {
        console.log("WebSocket disconnected");
        setWsConnected(false);
        reconnectTimerRef.current = setTimeout(connect, 3000);
      };

      ws.onerror = () => {
        ws.close();
      };
    }

    connect();

    return () => {
      if (wsRef.current) wsRef.current.close();
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
    };
  }, []);

  // Initial load — uses fetchDataRef so this effect doesn't refire when the
  // memoised fetchData identity changes after a filter/dateRange tweak.
  useEffect(() => {
    if (fetchDataRef.current) fetchDataRef.current();
  }, []);

  // Refetch when filters change
  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    fetchData(newFilters);
  };

  // Refetch when date range changes
  const handleDateRangeChange = (newRange) => {
    setDateRange(newRange);
    fetchData(filters, newRange);
  };

  // Fallback polling (30s) in case WebSocket drops
  useEffect(() => {
    const interval = setInterval(() => {
      if (!wsConnected && fetchDataRef.current) fetchDataRef.current();
    }, 30000);
    return () => clearInterval(interval);
  }, [wsConnected]);

  const sessionCount = sessions.length;

  // Tabs are now driven by configurable permissions. Admin always has every
  // permission; manager/associate access is controlled from the Role
  // Permissions tab (admin-only) which writes to the `role_permissions` table
  // and broadcasts the matrix through the user's `effective_permissions` array
  // on login + every /me call.
  const role = user?.role || "associate";
  const tabs = [
    { key: "sessions",      label: "Sessions",              perm: "tab.sessions" },
    { key: "integration",   label: "WordPress Integration", perm: "tab.integration_wp" },
    { key: "shopify",       label: "Shopify Integration",   perm: "tab.integration_shopify" },
    { key: "tester",        label: "Test Integration",      perm: "tab.tester" },
    { key: "coupons",       label: "Coupons",               perm: "tab.coupons" },
    { key: "prices",        label: "Live Prices",           perm: "tab.prices" },
    { key: "scraper",       label: "Scraper URLs",          perm: "tab.scraper_urls" },
    { key: "users",         label: "User Access",           perm: "tab.users" },
    { key: "scoring",       label: "Scoring Rules",         perm: "tab.scoring_rules" },
    { key: "knowledge",     label: "Knowledge Base",        perm: "tab.knowledge_base" },
    { key: "settings",      label: "API Settings",          perm: "tab.settings" },
    { key: "permissions",   label: "Role Permissions",      perm: "tab.role_permissions" },
  ].filter(t => hasPermission(t.perm));

  // If the active tab is no longer visible to this user (e.g. after a role
  // change), fall back to the first allowed tab.
  useEffect(() => {
    if (!tabs.some(t => t.key === activeTab)) {
      setActiveTab(tabs[0]?.key || "sessions");
    }
  }, [tabs, activeTab]);

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <Toaster position="top-right" theme="dark" richColors />

      {/* Header */}
      <header className="sticky top-0 z-40 bg-[#050505]/95 backdrop-blur border-b border-[#1A1A1A]">
        <div className="max-w-[1600px] mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl font-bold tracking-tight" data-testid="dashboard-title">
                Portl Sales Agent
              </h1>
              <p className="text-xs text-[#707070] mt-0.5">Command Centre</p>
            </div>
            <div className="flex items-center gap-4">
              <DateRangePicker dateRange={dateRange} onDateRangeChange={handleDateRangeChange} />
              <LiveIndicator connected={wsConnected} />
              <div className="flex items-center gap-3 pl-3 border-l border-[#2A2A2A]">
                <div className="text-right">
                  <p className="text-xs text-white font-medium" data-testid="user-display-name">
                    {user?.name || user?.email?.split("@")[0]}
                  </p>
                  <p className="text-[10px] text-[#666]">{user?.role}</p>
                </div>
                <button
                  onClick={logout}
                  data-testid="logout-button"
                  className="p-2 rounded hover:bg-[#FF3B30]/15 transition-colors group"
                  title="Sign out"
                >
                  <SignOut size={16} weight="bold" className="text-[#888] group-hover:text-[#FF3B30] transition-colors" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="max-w-[1600px] mx-auto px-6 py-6">
        {/* KPIs */}
        <KPIHeader kpis={kpis} loading={loading} />

        {/* Tabs */}
        <div className="flex items-center gap-1 mb-6 border-b border-[#1A1A1A]">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              data-testid={`tab-${tab.key}`}
              className={`px-4 py-2.5 text-sm font-medium transition-colors relative ${
                activeTab === tab.key
                  ? "text-[#E85D24]"
                  : "text-[#707070] hover:text-white"
              }`}
            >
              {tab.label}
              {activeTab === tab.key && (
                <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#E85D24]" />
              )}
            </button>
          ))}
          <div className="ml-auto text-xs text-[#555]" data-testid="session-count">
            {sessionCount} session{sessionCount !== 1 ? "s" : ""}
          </div>
        </div>

        {/* Tab content — each panel is also gated by permission for defence-in-depth */}
        {activeTab === "sessions" && (
          <>
            <SessionFilters filters={filters} onFilterChange={handleFilterChange} />
            <SessionsTable sessions={sessions} loading={loading} apiUrl={API} onSessionClick={(id) => setSelectedSessionId(id)} />
          </>
        )}
        {activeTab === "integration" && hasPermission("tab.integration_wp") && (
          <IntegrationTab apiUrl={BACKEND_URL} />
        )}
        {activeTab === "shopify" && hasPermission("tab.integration_shopify") && (
          <ShopifyIntegrationTab apiUrl={BACKEND_URL} />
        )}
        {activeTab === "tester" && hasPermission("tab.tester") && (
          <IntegrationTester />
        )}
        {activeTab === "coupons" && hasPermission("tab.coupons") && (
          <CouponManagement />
        )}
        {activeTab === "prices" && hasPermission("tab.prices") && (
          <LivePricesTab />
        )}
        {activeTab === "scraper" && hasPermission("tab.scraper_urls") && (
          <ScraperUrlsTab />
        )}
        {activeTab === "users" && hasPermission("tab.users") && (
          <UserManagement />
        )}
        {activeTab === "settings" && hasPermission("tab.settings") && (
          <AdminSettingsTab />
        )}
        {activeTab === "scoring" && hasPermission("tab.scoring_rules") && (
          <ScoringRulesTab />
        )}
        {activeTab === "permissions" && hasPermission("tab.role_permissions") && (
          <RolePermissionsTab />
        )}
        {activeTab === "knowledge" && hasPermission("tab.knowledge_base") && (
          <KnowledgeBaseTab />
        )}
      </main>

      {/* Session Detail Modal */}
      {selectedSessionId && (
        <SessionDetailModal
          sessionId={selectedSessionId}
          onClose={() => setSelectedSessionId(null)}
        />
      )}

      {/* Chat Widget */}
      <ChatWidget position="bottom-right" apiUrl={API} />
    </div>
  );
}
